import React from 'react';

const CARD_CONFIGS = [
  { key: 'totalAddresses', label: 'Total Addresses', format: 'number', positive: true },
  { key: 'totalDataPoints', label: 'Total Data Points', format: 'number', positive: true },
  { key: 'trackingSinceDays', label: 'Days Tracking', format: 'days', positive: true },
  { key: 'avgPriceChange', label: 'Avg Price Change', format: 'percent', positive: false },
  { key: 'addressesWithIncrease', label: 'Addresses w/ Increase', format: 'number', positive: false },
  { key: 'daysSinceLastChange', label: 'Days Since Last Change', format: 'days', positive: true },
];

const styles = {
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    transition: 'box-shadow 0.2s ease',
  },
  value: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  indicator: {
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
  },
};

function formatValue(value, format) {
  if (value == null) return '--';
  switch (format) {
    case 'percent':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    case 'days':
      return `${value}`;
    case 'number':
    default:
      return typeof value === 'number' ? value.toLocaleString() : String(value);
  }
}

function getTrend(key, value) {
  if (value == null) return { direction: 'neutral', color: '#6b7280' };

  switch (key) {
    case 'avgPriceChange':
      if (value > 0) return { direction: 'up', color: '#dc2626' };
      if (value < 0) return { direction: 'down', color: '#16a34a' };
      return { direction: 'neutral', color: '#6b7280' };
    case 'addressesWithIncrease':
      if (value > 0) return { direction: 'up', color: '#dc2626' };
      return { direction: 'neutral', color: '#16a34a' };
    case 'daysSinceLastChange':
      if (value <= 7) return { direction: 'neutral', color: '#f59e0b' };
      return { direction: 'neutral', color: '#16a34a' };
    case 'totalAddresses':
    case 'totalDataPoints':
    case 'trackingSinceDays':
      return { direction: 'neutral', color: '#2563eb' };
    default:
      return { direction: 'neutral', color: '#6b7280' };
  }
}

function TrendArrow({ direction, color }) {
  if (direction === 'up') {
    return (
      <span style={{ ...styles.indicator, color }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 2L12 8H2L7 2Z" fill={color} />
        </svg>
      </span>
    );
  }
  if (direction === 'down') {
    return (
      <span style={{ ...styles.indicator, color }}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 12L2 6H12L7 12Z" fill={color} />
        </svg>
      </span>
    );
  }
  return (
    <span style={{ ...styles.indicator, color }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <circle cx="7" cy="7" r="3" fill={color} />
      </svg>
    </span>
  );
}

export default function KPICards({ kpis }) {
  if (!kpis) return null;

  return (
    <div style={styles.container} data-kpi-grid="">
      {CARD_CONFIGS.map(({ key, label, format }) => {
        const value = kpis[key];
        const trend = getTrend(key, value);
        return (
          <div key={key} style={styles.card}>
            <div style={{ ...styles.value, color: trend.color }}>
              {formatValue(value, format)}
            </div>
            <div style={styles.labelRow}>
              <span style={styles.label}>{label}</span>
              <TrendArrow direction={trend.direction} color={trend.color} />
            </div>
          </div>
        );
      })}

      <style>{`
        @media (max-width: 1024px) {
          div[data-kpi-grid] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          div[data-kpi-grid] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
